// usePitchCanvas — the main entry point for 2D pitch rendering.
//
// Shared between:
//  - Reporting app (continuous: false) — static pitch maps, redraw on data change
//  - Coding app (continuous: true) — 60fps real-time player tracking
//
// DESIGN DECISIONS:
//  - Dual render mode via `continuous` flag instead of two separate hooks, because
//    95% of the logic (DPR sizing, resize observation, coordinate transforms,
//    zoom animation) is identical between the two modes.
//  - onDraw stored in a ref so the render loop always calls the latest version
//    without triggering effect re-runs. Safe to close over changing props.
//  - Zoom animation uses lerp interpolation (default 0.08 factor) for smooth
//    drill-focus transitions in the coding app. The reporting app doesn't use zoom.
//  - ResizeObserver triggers redraws when the container resizes (responsive layout).
//  - Canvas sized at width * devicePixelRatio for crisp rendering on retina displays.
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefCallback,
  type RefObject
} from 'react'
import { CANVAS_PADDING } from './constants'
import { computePitchBounds, createTransform } from './transform'
import type { PitchBounds, PitchCoordinates, PitchTransform } from './types'

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

function boundsClose(a: PitchBounds, b: PitchBounds, epsilon = 0.01): boolean {
  return (
    Math.abs(a.minX - b.minX) < epsilon &&
    Math.abs(a.maxX - b.maxX) < epsilon &&
    Math.abs(a.minY - b.minY) < epsilon &&
    Math.abs(a.maxY - b.maxY) < epsilon
  )
}

export interface UsePitchCanvasOptions {
  /** Pitch coordinates — bounds computed automatically. Required. */
  coordinates: PitchCoordinates | null
  /** Optional zoom target. When set, the view animates to focus on these bounds.
   *  When cleared (null), animates back to full pitch. Used by coding app for drill zoom. */
  focusBounds?: PitchBounds | null
  /** Meters of margin around focusBounds (default 5). */
  focusPadding?: number
  /** Canvas padding in CSS pixels (default 20). */
  padding?: number
  /** true = continuous rAF render loop (coding: real-time playback).
   *  false = on-demand via redraw() (reporting: static visualization).
   *  Default: false. */
  continuous?: boolean
  /** Lerp factor for zoom animation (default 0.08). */
  zoomSpeed?: number
  /** Draw callback. Stored in a ref — always calls the latest version
   *  without re-triggering effects. Safe to close over changing props. */
  onDraw: (
    ctx: CanvasRenderingContext2D,
    transform: PitchTransform,
    width: number,
    height: number
  ) => void
}

export interface UsePitchCanvasReturn {
  canvasRef: RefObject<HTMLCanvasElement | null>
  containerRef: RefCallback<HTMLDivElement>
  /** Latest transform for click handling / external coordinate conversion. */
  transform: PitchTransform | null
  /** Schedule a redraw on next animation frame. Coalesces multiple calls. */
  redraw: () => void
}

export function usePitchCanvas(
  options: UsePitchCanvasOptions
): UsePitchCanvasReturn {
  const {
    coordinates,
    focusBounds = null,
    focusPadding = 5,
    padding = CANVAS_PADDING,
    continuous = false,
    zoomSpeed = 0.08
  } = options

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerElRef = useRef<HTMLDivElement | null>(null)
  const [transform, setTransform] = useState<PitchTransform | null>(null)

  // Store onDraw in a ref so the loop always calls the latest version
  const onDrawRef = useRef(options.onDraw)
  onDrawRef.current = options.onDraw

  const paddingRef = useRef(padding)
  paddingRef.current = padding

  // Compute and cache pitch bounds from coordinates
  const pitchBoundsRef = useRef<PitchBounds | null>(null)
  const pitchBoundsChanged =
    coordinates &&
    Object.keys(coordinates).length > 0 &&
    (() => {
      const next = computePitchBounds(coordinates)
      const prev = pitchBoundsRef.current
      pitchBoundsRef.current = next
      return !prev || !boundsClose(prev, next)
    })()

  // Animation state — all in refs to avoid React re-renders during lerp
  const animatedBoundsRef = useRef<PitchBounds | null>(null)
  const targetBoundsRef = useRef<PitchBounds | null>(null)
  const rafIdRef = useRef<number>(0)
  const continuousRafRef = useRef<number>(0)
  const redrawRequestedRef = useRef(false)

  // Initialize animated bounds from pitch bounds (runs once when coordinates
  // are available). When the underlying pitch bounds change (e.g. the
  // reporting app swaps players and the pass-chart bounds recompute),
  // snap animated + target to the new bounds so the next draw uses the
  // fresh transform. Without this the old player's bounds stay cached in
  // `animatedBoundsRef` and the new data renders offscreen/squished.
  if (pitchBoundsRef.current) {
    if (!animatedBoundsRef.current || (pitchBoundsChanged && !focusBounds)) {
      animatedBoundsRef.current = { ...pitchBoundsRef.current }
      targetBoundsRef.current = { ...pitchBoundsRef.current }
    }
  }

  // Internal draw function — reads dimensions directly from DOM
  const performDraw = useCallback(() => {
    const canvas = canvasRef.current
    const container = containerElRef.current
    if (!canvas || !container) return

    const width = container.clientWidth
    const height = container.clientHeight
    if (width === 0 || height === 0) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const bounds = animatedBoundsRef.current ?? pitchBoundsRef.current
    if (!bounds) return

    // DPR-aware sizing
    const dpr = window.devicePixelRatio || 1
    canvas.width = width * dpr
    canvas.height = height * dpr
    canvas.style.width = `${width}px`
    canvas.style.height = `${height}px`
    ctx.scale(dpr, dpr)

    const t = createTransform(bounds, width, height, paddingRef.current)
    setTransform(t)

    onDrawRef.current(ctx, t, width, height)
  }, [])

  // On-demand redraw — coalesces multiple calls into one rAF
  const redraw = useCallback(() => {
    if (continuous) return
    if (redrawRequestedRef.current) return

    redrawRequestedRef.current = true
    requestAnimationFrame(() => {
      redrawRequestedRef.current = false
      performDraw()
    })
  }, [continuous, performDraw])

  // Container ref callback — attach ResizeObserver and trigger initial draw
  const observerRef = useRef<ResizeObserver | null>(null)

  const containerRef: RefCallback<HTMLDivElement> = useCallback(
    (el: HTMLDivElement | null) => {
      // Cleanup previous observer
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }

      containerElRef.current = el
      if (!el) return

      // ResizeObserver triggers redraws on size changes
      const observer = new ResizeObserver(() => {
        if (continuous) return // continuous mode draws every frame anyway
        redraw()
      })
      observer.observe(el)
      observerRef.current = observer

      // Schedule initial draw (el is now in the DOM with layout)
      requestAnimationFrame(() => performDraw())
    },
    [continuous, performDraw, redraw]
  )

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      observerRef.current?.disconnect()
    }
  }, [])

  // Compute target bounds when focusBounds changes
  useEffect(() => {
    const pitchB = pitchBoundsRef.current
    if (!pitchB) return

    if (focusBounds) {
      targetBoundsRef.current = {
        minX: focusBounds.minX - focusPadding,
        maxX: focusBounds.maxX + focusPadding,
        minY: focusBounds.minY - focusPadding,
        maxY: focusBounds.maxY + focusPadding
      }
    } else {
      targetBoundsRef.current = { ...pitchB }
    }

    // Initialize animated bounds if not yet set
    if (!animatedBoundsRef.current) {
      animatedBoundsRef.current = { ...targetBoundsRef.current }
      return
    }

    // Start zoom animation loop. In continuous mode the render loop
    // redraws every frame, so the animate step only needs to mutate
    // `animatedBoundsRef`. In on-demand mode (reporting app) nothing
    // else is redrawing, so we call `performDraw()` each frame while
    // the bounds are still easing — otherwise the zoom freezes on the
    // first lerp step and only resumes on the next external redraw.
    const animate = () => {
      const animated = animatedBoundsRef.current
      const target = targetBoundsRef.current
      if (!animated || !target) return

      if (boundsClose(animated, target)) {
        animatedBoundsRef.current = { ...target }
        if (!continuous) performDraw()
        rafIdRef.current = 0
        return
      }

      const t = zoomSpeed
      animatedBoundsRef.current = {
        minX: lerp(animated.minX, target.minX, t),
        maxX: lerp(animated.maxX, target.maxX, t),
        minY: lerp(animated.minY, target.minY, t),
        maxY: lerp(animated.maxY, target.maxY, t)
      }

      if (!continuous) performDraw()
      rafIdRef.current = requestAnimationFrame(animate)
    }

    if (!rafIdRef.current) {
      rafIdRef.current = requestAnimationFrame(animate)
    }

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = 0
      }
    }
  }, [focusBounds, focusPadding, zoomSpeed, continuous, performDraw])

  // Continuous render loop (coding app — real-time playback)
  useEffect(() => {
    if (!continuous) return

    const loop = () => {
      performDraw()
      continuousRafRef.current = requestAnimationFrame(loop)
    }
    continuousRafRef.current = requestAnimationFrame(loop)

    return () => cancelAnimationFrame(continuousRafRef.current)
  }, [continuous, performDraw])

  return { canvasRef, containerRef, transform, redraw }
}
